import { useMemo, useState } from 'react'
import Icon from './Icon.jsx'
import { formatShort, weekdayName } from '../lib/dates.js'
import { useEscapeKey } from '../lib/useEscapeKey.js'

/**
 * Every internal note on the board you are currently looking at, in one column.
 *
 * The notes themselves live on the bookings, which is where they are written and where the
 * tooltip surfaces them — but a note on a booking is only findable by hovering the booking,
 * and "what did anyone flag this fortnight" is a question about the whole range at once.
 * Hence a drawer rather than another column on the board: it answers a different question
 * from the one the grid answers, and it does not cost the grid any width to do it.
 *
 * Scope is deliberately exactly what is on screen. The board has already been fetched for
 * the current mode and date range and already been narrowed to what this person may see, so
 * reading the notes back out of it means the drawer cannot show a note from a day that is
 * not in view, from the other board, or from a crew this actor was not shown. Re-querying
 * would reintroduce all three as bugs waiting to happen.
 */
export default function InternalNotesDrawer({ board, onClose, onSelect }) {
  const [query, setQuery] = useState('')
  useEscapeKey(onClose)

  const staffById = useMemo(
    () => new Map((board?.staff || []).map((s) => [s.id, s])),
    [board],
  )
  const groupsById = useMemo(
    () => new Map((board?.groups || []).map((g) => [g.id, g])),
    [board],
  )

  // Date ascending, then down the page the way the board itself reads: by crew, then by the
  // job column. A note is easiest to place when the order of the list matches the order of
  // the thing it is about.
  const notes = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const rows = (board?.assignments || []).filter((a) => {
      if (!a.internalNote) return false
      if (!needle) return true
      // The note, the person and the job number. Somebody looking for a note remembers one
      // of those three, and rarely which one it was written against.
      const haystack = [a.internalNote, staffById.get(a.staffId)?.name, a.label]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(needle)
    })
    return rows.sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (staffById.get(a.staffId)?.name || '').localeCompare(staffById.get(b.staffId)?.name || '') ||
        a.slot - b.slot,
    )
  }, [board, staffById, query])

  // Grouped under one heading per day, so a fortnight does not repeat the same date sixteen
  // times down the left edge.
  const byDate = useMemo(() => {
    const m = new Map()
    for (const n of notes) {
      if (!m.has(n.date)) m.set(n.date, [])
      m.get(n.date).push(n)
    }
    return [...m.entries()]
  }, [notes])

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        className="drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Internal notes"
      >
        <div className="drawer-head">
          <div className="drawer-title">
            <strong>Internal notes</strong>
            <span>
              {query.trim()
                ? `${notes.length} match${notes.length === 1 ? '' : 'es'}`
                : notes.length === 0
                  ? 'Nothing flagged in this range'
                  : `${notes.length} note${notes.length === 1 ? '' : 's'} · ${formatShort(board.from)} – ${formatShort(board.to)}`}
            </span>
          </div>
          <button type="button" className="cell-pop-x" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="drawer-search">
          <Icon name="search" size={13} />
          <input
            type="search"
            value={query}
            placeholder="Search notes, people or jobs…"
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="cell-pop-x" onClick={() => setQuery('')} aria-label="Clear search">
              <Icon name="close" size={13} strokeWidth={2.2} />
            </button>
          )}
        </div>

        <div className="drawer-body">
          {byDate.length === 0 && (
            <div className="drawer-empty">
              <span className="drawer-empty-emoji" role="img" aria-hidden="true">
                {query.trim() ? '🔍' : '🗒️'}
              </span>
              {/* Two different emptinesses, and telling them apart is the whole value: one
                  means nobody has flagged anything, the other means your search missed. */}
              <p>{query.trim() ? 'No notes match that' : 'No internal notes yet'}</p>
            </div>
          )}

          {byDate.map(([date, rows]) => (
            <section key={date} className="drawer-day">
              <h4 className="drawer-day-head">
                {weekdayName(date)} {formatShort(date)}
              </h4>
              {rows.map((n) => {
                const group = groupsById.get(n.renderGroupId)
                // A button, not a div with a click: this moves the board underneath, which
                // is a real action and has to be reachable from the keyboard. The cell is
                // resolved by Board, which is the only place that knows how the board is
                // currently grouped and therefore what the cell is keyed by.
                return (
                  <article key={n.id} className="note-card">
                    <button
                      type="button"
                      className="note-card-hit"
                      title="Show this on the board"
                      onClick={() => onSelect?.(n)}
                    >
                    <div className="note-card-head">
                      {group?.color && (
                        <span className="group-swatch" style={{ background: group.color }} />
                      )}
                      <strong>{staffById.get(n.staffId)?.name || 'Unknown'}</strong>
                      <span className="note-card-job">{n.label}</span>
                    </div>
                    {/* Whitespace preserved: these are typed as short paragraphs or as a
                        list of lines, and reflowing them into one run loses the shape the
                        person who wrote it gave it. */}
                    <p className="note-card-body">{n.internalNote}</p>
                    </button>
                  </article>
                )
              })}
            </section>
          ))}
        </div>
      </aside>
    </div>
  )
}
